import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import withRouter from "components/Common/withRouter";
import TableContainer from "../../components/Common/TableContainer";
import {tmData} from "../../common/data/demo/threat-modeling/taskData"; 
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Button,
} from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "components/Common/Breadcrumb";
import DeleteModal from "components/Common/DeleteModal";

// Redux
import { useSelector, useDispatch } from "react-redux";
import Spinners from "components/Common/Spinner";
import { ToastContainer } from "react-toastify";

const TMhistory = () => {
  document.title = "Tasks";

  const dispatch = useDispatch();

  const [taskData, setTaskData] = useState([]); // State to store fetched task data
  const [isLoading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null); // For delete modal
  const [viewModal, setViewModal] = useState(false); // For view task modal
  const [taskDetails, setTaskDetails] = useState(null); // To hold the selected task details
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username; // Replace with your user ID

  const EmptyState = () => (
    <div className="text-center p-5">
      <div className="mb-4">
        <i className="mdi mdi-file-search text-primary" style={{ fontSize: "4rem" }} />
      </div>
      <h3 className="mb-3 font-weight-bold">No Scans Found</h3>
      <p className="text-muted mb-4">
        You haven't performed any ThreatModelling yet. Start by analyzing a configuration to detect potential threats.
      </p>
      <div className="d-flex justify-content-center">
        <Link to="/tm-create" className="btn btn-primary px-4 py-2">
          Start New Scan
        </Link>
      </div>
    </div>
  );

  // Fetching task data from the API
  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        const response = await fetch(`https://api.compliance.cx/task-data/${userId}`);
        const data = await response.json();
        setTaskData(data.reverse());
      } catch (error) {
        if (userId === "demo") {
          setTaskData(tmData);
        }
        console.error("Error fetching task data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTaskData();
  }, []);

  // Handle delete action
  const onClickDelete =async (scanId) => {
    try {
      // Prepare the request body with "Malware Scan" as the category
      const requestBody = {
        scanId: scanId,  // Use scanId
        category: "Threat Modeling",  // Set category to "Malware Scan"
      };
      console.log("Request body:", requestBody);
      // Send the DELETE request to the API with the JSON body
      const response = await fetch(`https://api.compliance.cx/trash/${userId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json', // Set the content type
        },
        body: JSON.stringify(requestBody), // Send the JSON payload
      });
      console.log("Response:", response.text());
      if (response.ok) {
        // If deletion is successful, remove the task from the list in state
        setTaskData(prevTasks => prevTasks.filter(task => task.scanId !== scanId));
        console.log('Task deleted successfully');
      } else {
        console.error('Error deleting task:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  // Handle view task action
  const onClickView = (task) => {
    setTaskDetails(task);
    setViewModal(true);
  };
  const formatAnalysisContent = (content) => {
    if (content) {
      // Split content by line breaks
      return content.split('\n').map((line, index) => {
        // Remove leading and trailing spaces
        line = line.trim();

        // Handle bullet points (lines starting with `*`)
        if (line.startsWith('*')) {
          return (
            <div key={index} style={{ marginLeft: '20px' }}>
              <span>•</span> {parseBoldText(line.slice(1).trim())}
            </div>
          );
        }

        // For bold text (`**`)
        else if (line.includes('**')) {
          return (
            <div key={index}>
              {parseBoldText(line)} {/* Process bold text */}
            </div>
          );
        }

        // Regular text line
        else {
          return (
            <div key={index}>
              {line}
            </div>
          );
        }
      });
    }
    return <p>Analysis is not complete yet.</p>;
  };
  const parseBoldText = (line) => {
    return line.replace(/\*\*(.*?)\*\*/g, (match, p1) => {
      return `${p1}`; // Replace bold text markers with HTML <strong>
    });
  };


  // Table Columns
  const columns = useMemo(
    () => [
      {
        header: "Profile ID",
        accessorKey: "scanId", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Asset Name",
        accessorKey: "assetName", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Description",
        accessorKey: "description", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Initiated",
        accessorKey: "initiated", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Last Scan",
        accessorKey: "lastScan", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Status",
        accessorKey: "status", // Ensure correct key from JSON
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return (
            <Badge className={
              cellProps.row.original.status.toLowerCase() === "new" ? "bg-info" :
                cellProps.row.original.status.toLowerCase() === "approved" ? "bg-success" :
                  cellProps.row.original.status.toLowerCase() === "unapproved" ? "bg-danger" :
                    "bg-secondary"  // Default in case none of the conditions match
            }>

              {cellProps.row.original.status}
            </Badge>
          );
        },
      },
      {
        header: "Resolved",
        accessorKey: "resolved",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => {
          const { resolved, scanId } = cellProps.row.original;
      
          // If resolved is null or undefined, show a placeholder
          if (resolved === null || resolved === undefined) {
            return <span>-</span>;
          }
      
          // Function to toggle resolved status
          const toggleResolvedStatus = async () => {
            try {
              const newResolvedStatus = !resolved;
      
              // Update the state optimistically
              setTaskData((prevTaskData) =>
                prevTaskData.map((task) =>
                  task.scanId === scanId ? { ...task, resolved: newResolvedStatus } : task
                )
              );
      
              // Make the API call to update the resolved status
              const response = await fetch(
                `https://api.compliance.cx/threat-modeling/${userId}/results/${scanId}`,
                {
                  method: "PATCH",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ scanId, resolved: newResolvedStatus }),
                }
              );
      
              if (!response.ok) {
                console.error("Failed to update resolved status:", response.statusText);
      
                // Revert state if the API call fails
                setTaskData((prevTaskData) =>
                  prevTaskData.map((task) =>
                    task.scanId === scanId ? { ...task, resolved } : task
                  )
                );
              }
            } catch (error) {
              console.error("Error updating resolved status:", error);
      
              // Revert state on error
              setTaskData((prevTaskData) =>
                prevTaskData.map((task) =>
                  task.scanId === scanId ? { ...task, resolved } : task
                )
              );
            }
          };
      
          return (
            <Button
              color={resolved ? "success" : "danger"}
              size="sm"
              onClick={toggleResolvedStatus}
              disabled={resolved === null || resolved === undefined}
            >
              {resolved ? "Resolved" : "Unresolved"}
            </Button>
          );
        },
      },
      

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => {
          return (
            <div className="list-unstyled hstack gap-1 mb-0">
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-primary"
                  id={`viewtooltip${cellProps.row.original.scanId}`}
                  onClick={() => onClickView(cellProps.row.original)} // Open the view modal
                >
                  <i className="mdi mdi-eye-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`viewtooltip${cellProps.row.original.scanId}`}
                  >
                    View
                  </UncontrolledTooltip>
                </Link>
              </li>
              <li>
                <Link
                  to="#"
                  className="btn btn-sm btn-soft-danger"
                  onClick={() => {
                    const taskData = cellProps.row.original;
                    onClickDelete(taskData.scanId);
                  }}
                  id={`deletetooltip${cellProps.row.original.scanId}`}
                >
                  <i className="mdi mdi-delete-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`deletetooltip${cellProps.row.original.scanId}`}
                  >
                    Delete
                  </UncontrolledTooltip>
                </Link>
              </li>
              {/* <li>
                <Link
                  to={`/tm-edit/${cellProps.row.original.scanId}`}
                  className="btn btn-sm btn-soft-primary"
                  id={`edittooltip${cellProps.row.original.scanId}`}
                >
                  <i className="mdi mdi-pencil-outline" />
                  <UncontrolledTooltip
                    placement="top"
                    target={`edittooltip${cellProps.row.original.scanId}`}
                  >
                    Edit
                  </UncontrolledTooltip>
                </Link>
              </li> */}
            </div>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      
      {/* View Modal with adjusted size */}
      <Modal
        isOpen={viewModal}
        toggle={() => setViewModal(!viewModal)}
        size="lg"
        style={{ maxWidth: "80%", width: "80%", height: "80%", margin: "auto" }}
        centered
      >
        <ModalHeader toggle={() => setViewModal(false)}>
          {taskDetails ? taskDetails.scanId : 'Loading...'} {/* Displaying Task ID */}
        </ModalHeader>

        <ModalBody style={{ height: "70vh", overflowY: "auto" }}>
          {taskDetails ? (
            <div>
              <p><strong>Description:</strong> {taskDetails.description}</p>
              <p><strong>Initiated:</strong> {taskDetails.initiated}</p>
              <p><strong>Last Scan:</strong> {taskDetails.lastScan}</p>

              <div>
                <strong>Analysis:</strong>
                {taskDetails.analysis && taskDetails.analysis.trim() !== "" ? (
                  formatAnalysisContent(taskDetails.analysis) // Format and display analysis
                ) : (
                  <p className="text-warning">Analysis is not complete yet.</p>
                )}
              </div>
            </div>
          ) : (
            <div>Loading...</div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      <div className="page-content">
        <Container fluid>
          <Breadcrumbs breadcrumbItem="Tasks" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  {isLoading ? (
                    <div className="text-center p-4">
                      <Spinners setLoading={setLoading} />
                    </div>
                  ) : taskData.length === 0 ? (
                    <EmptyState />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={taskData}
                      isGlobalFilter={true}
                      isJobListGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search tasks..."
                      tableClass="table-bordered align-middle nowrap mt-2"
                      pagination="pagination pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default withRouter(TMhistory);
